import React, { useState, useEffect } from 'react'
import { isEqual } from 'lodash'

import { removeRoles } from '../../../utilities/api'
import { scrollToTopOfModal } from '../../../utilities/helpers'

import Stack from 'react-bootstrap/Stack'
import Button from 'react-bootstrap/Button'

import { Role } from './Role'
import {
  CreateRoleModal,
  PERMISSION_SLUG_DISPLAY_ORDER,
} from './CreateRoleModal'
import { DeleteModal } from '../../DeleteModal'
import { sortRolesAccountOwnerFirst } from './reservedRoleNames'

const ROLE_MODAL_VISIBLE_SLUG_SET = new Set(PERMISSION_SLUG_DISPLAY_ORDER)

function filterToRoleModalVisible(items) {
  if (!Array.isArray(items)) return []
  return items.filter((p) => p?.slug && ROLE_MODAL_VISIBLE_SLUG_SET.has(p.slug))
}

/** One list of `{ id, slug, name }` from every CMS permission group key (see formatPermissions). */
export function flattenGroupedPermissions(permissions) {
  if (!permissions || typeof permissions !== 'object') return []
  return Object.values(permissions).flatMap((group) =>
    Array.isArray(group) ? group : []
  )
}

export default function Roles({
  canManageTeam,
  roles,
  permissions,
  createRoles,
  updateRoles,
  isCreating,
  sortRolesForSelect = sortRolesAccountOwnerFirst,
  nameMatchesReservedAccountOwner,
  nameMatchesScannerOrganizationRole,
  nameMatchesHiddenOrganizationRole,
}) {
  // since isCheckAll state is asynchronous, this flag tells if individual checkboxes were checked before the Select All checkbox
  let justIsCheckAll = false

  // new and existing role so objects match for controlled input fields
  const [role, setRole] = useState('')

  // for editing - save role id for flag when sending original role when editing
  // for deleting
  const [id, setId] = useState()

  const [initialEditState, setInitialEditState] = useState()

  const [isNameValid, setIsNameValid] = useState(true)

  const [isCheckAll, setIsCheckAll] = useState(false)

  const [isCheck, setIsCheck] = useState([])

  const [showCreate, setShowCreate] = useState(false)

  const [showDelete, setShowDelete] = useState(false)

  const [isDisabled, setIsDisabled] = useState(false)

  const [isRemoving, setIsRemoving] = useState(false)

  // reset permissions when adding or editing
  useEffect(() => {
    const isEdit = id && role
    // if adding role
    if (!isEdit) setIsCheck([])
    // if editing existing role
    if (isEdit) {
      setIsCheck([
        ...isCheck,
        ...role?.organization_permissions?.map(
          (item) => item.slug ?? item.attributes?.slug
        ),
      ])

      // set initial state if editing
      setInitialEditState({
        role,
        permissions: [
          ...role?.organization_permissions?.map(
            (item) => item.slug ?? item.attributes?.slug
          ),
        ],
      })
    }
  }, [id])

  useEffect(() => {
    // if no checkboxes are checked
    if (isCheck.length === 0) setIsCheckAll(false)
  }, [isCheck])

  // keep checking properties within initial state object when it changes
  useEffect(() => {
    setIsDisabled(
      (isEqual(initialEditState?.role, role) &&
        (initialEditState?.permissions ?? []).every((i) =>
          isCheck.includes(i)
        ) &&
        (initialEditState?.permissions ?? []).length === isCheck.length) ||
        !role?.name ||
        !isNameValid ||
        isCreating
    )
  }, [initialEditState, isCheck, role?.name, isNameValid, isCreating])

  // reset validation
  useEffect(() => {
    if (!isNameValid) setIsNameValid(true)
  }, [role.name])

  useEffect(() => {
    if (!isNameValid) {
      scrollToTopOfModal()
    }
  }, [isNameValid])

  // replace one or more space with ""
  const handleValid = () => {
    if (isReservedRoleName()) {
      setIsNameValid(false)
    }
  }

  const isReservedRoleName = () =>
    nameMatchesReservedAccountOwner(role?.name) ||
    nameMatchesScannerOrganizationRole(role?.name)

  // handle create and edit
  const handleShowCreate = (_, role) => {
    setShowCreate(true)
    if (role) {
      setId(role?.id)
      setRole(role)
    }
  }

  const handleCloseCreate = () => {
    setShowCreate(false)
    setIsCheck([])
    setRole('')
    setId()
    setInitialEditState()
  }

  const handleShowDelete = (id) => {
    setShowDelete(true)
    setId(id)
  }

  const handleCloseDelete = () => {
    setShowDelete(false)
    setId()
  }

  const handleCheck = (e) => {
    const { id, checked } = e.target
    setIsCheckAll(false)
    justIsCheckAll = false
    if (checked) {
      setIsCheck([...isCheck, id])
    } else {
      setIsCheck(isCheck.filter((item) => item !== id))
    }
  }

  const handleSelectAll = (e) => {
    const { name, checked } = e.target

    const obj = name
      ? (permissions?.[`${name}`] ?? [])
      : flattenGroupedPermissions(permissions)

    const list = filterToRoleModalVisible(Array.isArray(obj) ? obj : [])

    if (checked) {
      // set flag to say that other categories have been checked - is true on next update since it is asynchronous
      setIsCheckAll(true)
      // synchronous flag
      justIsCheckAll = true

      if (name) {
        // if other categories are checked, add items to selected, if not just replace
        if (!isCheckAll && !justIsCheckAll)
          setIsCheck(list.map((item) => item.slug))
        // other categories are checked
        else {
          // filter out items that are already checked
          let itemsToAdd = list.filter((item) => !isCheck.includes(item.slug))
          setIsCheck([...isCheck, ...itemsToAdd.map((item) => item.slug)])
        }
      }
      // select all categories
      else {
        setIsCheck([...list.map((item) => item.slug)])
      }
    } else {
      const ids = list.map((item) => item.slug)
      // remove obj ids from array - check if obj id is in the isCheck array
      const filteredArray = isCheck.filter((i) => ids.indexOf(i) == -1)
      setIsCheck([...filteredArray])
    }
  }

  /** Pure read: whether every slug in `arr` is selected. No setState — `checked={checker(...)}` runs during child render. */
  const checker = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return false
    return arr.every((v) => isCheck.includes(v.slug))
  }

  const handleCreate = (e) => {
    e.preventDefault()
    if (isDisabled || isReservedRoleName() || isCheck?.length === 0) return
    createRoles({
      roleName: role?.name,
      permissions: isCheck,
      currentRole: id ? role : undefined,
    })
      .then(() => {
        handleCloseCreate()
      })
      .catch((err) => {
        console.error(err)
        setIsNameValid(false)
      })
  }

  const allPermissionsFlat = flattenGroupedPermissions(permissions)

  const visibleRoles = (roles ?? []).filter(
    (role) => !nameMatchesHiddenOrganizationRole(role?.name)
  )

  const rolesDisplayOrder = sortRolesForSelect(visibleRoles)

  const removeRole = () => {
    setIsRemoving(true)
    removeRoles({ data: { roleId: id } })
      .then((res) => {
        updateRoles(res.data)
        handleCloseDelete()
        setIsRemoving(false)
      })
      .catch((err) => {
        console.error(err)
        setIsRemoving(false)
      })
  }

  return (
    <>
      <div className="heading--flex-space-between mb-4">
        <h1 className="fs-md fs-md-bold">Roles</h1>
        <Button
          variant="outline-light"
          className="btn-plus"
          disabled={!canManageTeam}
          onClick={handleShowCreate}
        >
          Create a new role
        </Button>
      </div>
      <Stack as="ul" gap={2}>
        {rolesDisplayOrder.map((role) => (
          <Role
            key={role?.id}
            canManageTeam={canManageTeam}
            role={role}
            handleShowCreate={handleShowCreate}
            handleShowDelete={handleShowDelete}
            nameMatchesReservedAccountOwner={nameMatchesReservedAccountOwner}
            nameMatchesHiddenOrganizationRole={
              nameMatchesHiddenOrganizationRole
            }
          />
        ))}
      </Stack>

      <CreateRoleModal
        show={showCreate}
        handleClose={handleCloseCreate}
        permissions={permissions}
        allPermissionsFlat={allPermissionsFlat}
        id={id}
        role={role}
        setRole={setRole}
        handleValid={handleValid}
        isValid={isNameValid}
        isCheck={isCheck}
        checker={checker}
        handleSelectAll={handleSelectAll}
        handleCheck={handleCheck}
        handleCreate={handleCreate}
        isCreating={isCreating}
        isDisabled={isDisabled}
      />

      <DeleteModal
        entity="role"
        show={showDelete}
        handleClose={handleCloseDelete}
        id={role.id}
        isRemoving={isRemoving}
        handleDelete={removeRole}
      />
    </>
  )
}
